import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FiBriefcase,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUsers,
  FiHome,
  FiGrid,
  FiList,
  FiCheck,
  FiMoreVertical,
  FiUpload,
} from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import ImageCropper, { COMPANY_HEADER_CROP_ASPECT } from '../../User/Pages/Components/froms/ImageCropper';
import './AdminPages.css';
import './CompanyList.css';

const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

const CompanyList = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [creatorMenuKey, setCreatorMenuKey] = useState(null);
  const [headerSettings, setHeaderSettings] = useState({ default_image: '', default_image_url: null });
  const [headerSaving, setHeaderSaving] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropFileName, setCropFileName] = useState('');

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/companies`, {
        headers: headers(),
        params: { q: query || undefined, limit: 500 },
      });
      setCompanies(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [query]);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/companies/${id}`, { headers: headers() });
      setDetail(res.data || null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load company');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadHeaderSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/companies/header-settings`, { headers: headers() });
      setHeaderSettings({
        default_image: res.data?.default_image || '',
        default_image_url: res.data?.default_image_url || null,
      });
    } catch (_) {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadHeaderSettings();
  }, [loadHeaderSettings]);

  useEffect(() => {
    if (selectedId) {
      setCreatorMenuKey(null);
      loadDetail(selectedId);
    }
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (creatorMenuKey == null) return undefined;
    const onDoc = (e) => {
      if (e.target.closest?.('.company-creator-menu')) return;
      setCreatorMenuKey(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [creatorMenuKey]);

  const logoSrc = (logo) => {
    if (!logo) return '';
    if (/^https?:\/\//i.test(logo)) return logo;
    const base = API_URL.replace(/\/api\/?$/, '');
    const path = String(logo).replace(/^\/+/, '');
    return `${base}/${path.startsWith('uploads/') ? path : `uploads/${path}`}`;
  };

  const saveHeaderSettings = async (default_image) => {
    setHeaderSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/admin/companies/header-settings`,
        { default_image: default_image || '' },
        { headers: headers() }
      );
      setHeaderSettings({
        default_image: res.data?.default_image || '',
        default_image_url: res.data?.default_image_url || null,
      });
      toast.success(default_image ? 'Default header image saved' : 'Default header image cleared');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save default header image');
    } finally {
      setHeaderSaving(false);
    }
  };

  const uploadDefaultHeader = async (file) => {
    if (!file) return;
    const fm = new FormData();
    fm.append('file', file);
    setHeaderSaving(true);
    try {
      const res = await axios.post(`${API_URL}/upload/single`, fm, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      const path = res.data?.path || res.data?.filename || res.data?.url || '';
      await saveHeaderSettings(path);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Default header upload failed');
      setHeaderSaving(false);
    }
  };

  const openDefaultHeaderCrop = (file) => {
    if (!file || !file.type?.startsWith('image/')) return;
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropFileName(file.name || 'default-header.jpg');
    setCropSrc(URL.createObjectURL(file));
  };

  const closeDefaultHeaderCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setCropFileName('');
  };

  const handleDefaultHeaderCropDone = async (croppedFile) => {
    closeDefaultHeaderCrop();
    await uploadDefaultHeader(croppedFile);
  };

  const deleteCompany = async (id) => {
    if (!window.confirm('Delete this company? Assigned properties/projects will be kept, but their company profile link will be cleared.')) return;
    try {
      await axios.delete(`${API_URL}/admin/companies/${id}`, { headers: headers() });
      toast.success('Company deleted');
      if (selectedId === id) {
        setSelectedId(null);
        setDetail(null);
      }
      loadCompanies();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete company');
    }
  };

  const acceptDuplicate = async (id) => {
    setAccepting(true);
    try {
      const res = await axios.put(
        `${API_URL}/admin/companies/${id}/accept-duplicate`,
        {},
        { headers: headers() }
      );
      setDetail(res.data || null);
      toast.success('Accepted — kept as a separate company record');
      await loadCompanies();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to accept duplicate');
    } finally {
      setAccepting(false);
    }
  };

  const openListingAdmin = (propertyId) => {
    navigate(`/admin/properties?search=${encodeURIComponent(String(propertyId))}`);
  };

  const selected = companies.find((c) => c.id === selectedId);
  const isDuplicate = !!(detail?.company?.duplicate_pending || selected?.duplicate_pending);

  return (
    <div className="admin-page company-list-page">
      {cropSrc ? (
        <ImageCropper
          src={cropSrc}
          fileName={cropFileName}
          aspect={COMPANY_HEADER_CROP_ASPECT}
          title="Crop Default Header Image"
          onDone={handleDefaultHeaderCropDone}
          onCancel={closeDefaultHeaderCrop}
        />
      ) : null}
      <div className="page-header">
        <div>
          <h1><FiBriefcase style={{ marginRight: 8 }} />Company List</h1>
          <p>All companies/developers (including previous projects), listings, and creators.</p>
        </div>
        <div className="page-header-actions">
          <button type="button" className="btn-secondary" onClick={loadCompanies} disabled={loading}>
            <FiRefreshCw /> Refresh
          </button>
          <Link
            to="/admin/companies/new"
            className="btn-primary"
            id="add-new-company-btn"
            style={{ cursor: 'pointer', position: 'relative', zIndex: 22 }}
          >
            <FiPlus /> Add New Company
          </Link>
        </div>
      </div>

      <div className="company-default-header-card">
        <div className="company-default-header-copy">
          <h3>Default company header image</h3>
          <p>
            Shown on the right side of public company headers when a company has no custom image
            and “Show Default Image” is enabled.
          </p>
        </div>
        <div className="company-default-header-actions">
          <div className="company-default-header-preview">
            {headerSettings.default_image_url || headerSettings.default_image ? (
              <img
                src={headerSettings.default_image_url || logoSrc(headerSettings.default_image)}
                alt=""
              />
            ) : (
              <span>No default</span>
            )}
          </div>
          <label className="company-default-header-upload">
            <FiUpload /> {headerSaving ? 'Saving…' : 'Upload & crop'}
            <input
              type="file"
              accept="image/*"
              disabled={headerSaving}
              onChange={(e) => {
                openDefaultHeaderCrop(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          {headerSettings.default_image ? (
            <button
              type="button"
              className="company-default-header-clear"
              disabled={headerSaving}
              onClick={() => saveHeaderSettings('')}
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>

      <div className="company-layout">
        <aside className="company-sidebar admin-card">
          <div className="company-search">
            <FiSearch />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search companies…"
            />
          </div>
          {loading ? (
            <div className="skeleton" style={{ height: 220, borderRadius: 8 }} />
          ) : companies.length === 0 ? (
            <div className="company-empty">No companies yet.</div>
          ) : (
            <ul className="company-nav-list">
              {companies.map((c) => {
                const creatorTip = c.created_by_name
                  ? `Created by: ${c.created_by_name}`
                  : 'Created by: Unknown';
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      title={creatorTip}
                      className={`company-nav-item${selectedId === c.id ? ' is-active' : ''}${c.duplicate_pending ? ' is-duplicate' : ''}`}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <span className="company-nav-logo">
                        {c.logo_url || c.logo ? (
                          <img src={c.logo_url || logoSrc(c.logo)} alt="" />
                        ) : (
                          <span>{(c.name || '?').slice(0, 1).toUpperCase()}</span>
                        )}
                      </span>
                      <span className="company-nav-copy">
                        <strong title={creatorTip}>{c.name}</strong>
                        <small>
                          {c.listing_count || 0} listings · {c.property_count || 0} props · {c.project_count || 0} projects
                        </small>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <section className="company-main admin-card">
          {!selectedId && (
            <div className="company-empty">Select a company from the list, or create a new one.</div>
          )}

          {selectedId && (
            detailLoading ? (
              <div className="skeleton" style={{ height: 320, borderRadius: 8 }} />
            ) : detail ? (
              <div className="company-detail">
                <div className="company-detail-hero">
                  <div className="company-detail-hero-media">
                    {(detail.company?.effective_header_image_url
                      || detail.company?.header_image_url
                      || (detail.company?.header_image && logoSrc(detail.company.header_image))
                      || headerSettings.default_image_url
                      || (headerSettings.default_image && logoSrc(headerSettings.default_image))
                    ) ? (
                      <img
                        src={
                          detail.company?.effective_header_image_url
                          || detail.company?.header_image_url
                          || (detail.company?.header_image ? logoSrc(detail.company.header_image) : '')
                          || headerSettings.default_image_url
                          || logoSrc(headerSettings.default_image)
                        }
                        alt=""
                      />
                    ) : (
                      <div className="company-detail-hero-fallback" />
                    )}
                    <div className="company-detail-hero-shade" />
                  </div>

                  <div className="company-detail-hero-body">
                    <div className="company-detail-brand">
                      <div className="company-detail-logo">
                        {detail.company?.logo_url || detail.company?.logo ? (
                          <img src={detail.company.logo_url || logoSrc(detail.company.logo)} alt="" />
                        ) : (
                          <span>{(detail.company?.name || '?').slice(0, 2).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="company-detail-identity">
                        <div className="company-detail-title-row">
                          <h2
                            title={
                              detail.company?.created_by_name
                                ? `Created by: ${detail.company.created_by_name}`
                                : undefined
                            }
                          >
                            {detail.company?.name}
                          </h2>
                          <span className="company-detail-verified">Verified</span>
                        </div>
                        <p>{detail.company?.description || 'No description provided for this company.'}</p>
                        <div className="company-detail-meta">
                          {detail.company?.created_by_name ? (
                            <span>Created by {detail.company.created_by_name}</span>
                          ) : null}
                          {detail.company?.createdAt ? (
                            <span>
                              Joined {new Date(detail.company.createdAt).toLocaleString('en-US', { month: 'short', year: 'numeric' })}
                            </span>
                          ) : null}
                          {detail.company?.show_default_header_image !== false && !detail.company?.header_image ? (
                            <span>Using default header image</span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="company-detail-actions">
                      {isDuplicate && (
                        <button
                          type="button"
                          className="btn-primary company-accept-btn"
                          disabled={accepting}
                          onClick={() => acceptDuplicate(selectedId)}
                        >
                          <FiCheck /> {accepting ? 'Accepting…' : 'Accept'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => navigate(`/admin/companies/${selectedId}/edit`)}
                      >
                        Edit / Assign
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => deleteCompany(selectedId)}>
                        <FiTrash2 /> Delete
                      </button>
                    </div>
                  </div>
                </div>

                {isDuplicate && (
                  <div className="company-duplicate-banner">
                    Duplicate company/developer name — this is already a separate company record (not merged). Click Accept to finalize it as new company data and clear the highlight.
                  </div>
                )}

                <div className="company-stat-row">
                  <div className="company-stat"><FiHome /> <strong>{detail.stats?.properties || 0}</strong> Properties</div>
                  <div className="company-stat"><FiGrid /> <strong>{detail.stats?.projects || 0}</strong> Projects</div>
                  <div className="company-stat"><FiUsers /> <strong>{detail.stats?.creators || 0}</strong> Creators</div>
                </div>

                <div className="company-detail-section">
                  <div className="company-detail-section-head">
                    <h3>Additional Image</h3>
                    <small>
                      {detail.company?.header_image
                        ? 'Custom header image'
                        : (headerSettings.default_image || headerSettings.default_image_url
                          ? 'Default header image'
                          : 'No header image')}
                    </small>
                  </div>
                  <div className="company-detail-additional">
                    {(detail.company?.effective_header_image_url
                      || detail.company?.header_image_url
                      || (detail.company?.header_image && logoSrc(detail.company.header_image))
                      || headerSettings.default_image_url
                      || (headerSettings.default_image && logoSrc(headerSettings.default_image))
                    ) ? (
                      <img
                        src={
                          detail.company?.effective_header_image_url
                          || detail.company?.header_image_url
                          || (detail.company?.header_image ? logoSrc(detail.company.header_image) : '')
                          || headerSettings.default_image_url
                          || logoSrc(headerSettings.default_image)
                        }
                        alt=""
                      />
                    ) : (
                      <div className="company-empty">No additional / header image set. Upload a default in Company List settings.</div>
                    )}
                  </div>
                </div>

                <div className="company-detail-section">
                  <div className="company-detail-section-head">
                    <h3>Properties</h3>
                    <small>{(detail.properties || []).length} linked</small>
                  </div>
                  <div className="company-card-grid">
                    {(detail.properties || []).length === 0 && <div className="company-empty">No properties linked.</div>}
                    {(detail.properties || []).map((p) => (
                      <article key={`prop-${p.id}`} className="company-listing-card">
                        <div className="company-listing-thumb">
                          {p.thumbnail ? <img src={p.thumbnail} alt="" /> : <span>No image</span>}
                        </div>
                        <div className="company-listing-body">
                          <strong>{p.title}</strong>
                          <small>#{p.property_id} · {p.location || '—'}</small>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>

                <div className="company-detail-section">
                  <div className="company-detail-section-head">
                    <h3>Projects</h3>
                    <small>{(detail.projects || []).length} linked</small>
                  </div>
                  <div className="company-card-grid">
                    {(detail.projects || []).length === 0 && <div className="company-empty">No projects linked.</div>}
                    {(detail.projects || []).map((p) => (
                      <article key={`proj-${p.id}`} className="company-listing-card">
                        <div className="company-listing-thumb">
                          {p.thumbnail ? <img src={p.thumbnail} alt="" /> : <span>No image</span>}
                        </div>
                        <div className="company-listing-body">
                          <strong>{p.title}</strong>
                          <small>#{p.property_id} · {p.location || '—'}</small>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>

                <div className="company-detail-section">
                  <div className="company-detail-section-head">
                    <h3>Users Who Created Listings</h3>
                    <small>{(detail.creators || []).length} users</small>
                  </div>
                  <div className="company-creators">
                    {(detail.creators || []).length === 0 && <div className="company-empty">No creators found.</div>}
                    {(detail.creators || []).map((u, idx) => {
                      const pid = u.property_id;
                      const rowKey = `${u.listing_id || pid}-${idx}`;
                      const menuOpen = creatorMenuKey === rowKey;
                      return (
                        <div
                          key={rowKey}
                          className="company-creator-row"
                        >
                          <div className="company-creator-email">
                            <strong>{u.email || '—'}</strong>
                          </div>
                          <div className="company-creator-actions company-creator-menu">
                            <button
                              type="button"
                              className={`company-icon-btn${menuOpen ? ' is-open' : ''}`}
                              aria-label="Creator listing menu"
                              aria-expanded={menuOpen}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCreatorMenuKey(menuOpen ? null : rowKey);
                              }}
                            >
                              <FiMoreVertical />
                            </button>
                            {menuOpen && (
                              <div className="company-creator-tooltip" role="tooltip">
                                <span className="company-creator-tooltip-label">Property ID</span>
                                <strong>#{pid}</strong>
                                <button
                                  type="button"
                                  className="company-creator-tooltip-link"
                                  onClick={() => {
                                    setCreatorMenuKey(null);
                                    openListingAdmin(pid);
                                  }}
                                >
                                  <FiList /> Open listing
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null
          )}
        </section>
      </div>
    </div>
  );
};

export default CompanyList;
